<template>
  <div class="container py-3">
    <!-- Page Header -->
    <div class="page-header">
      <h2>{{ $t('purchaseOrder.title') }}</h2>
      <button class="btn btn-primary" @click="openAddModal">{{ $t('purchaseOrder.add') }}</button>
    </div>

    <!-- Search -->
    <div class="search-wrapper mb-3">
      <span class="search-icon">🔍</span>
      <input v-model="search" type="text" class="form-control" placeholder="Search purchase orders..." />
    </div>

    <!-- Purchase Orders Table -->
    <div class="table-card">
      <table class="premium-table table">
        <thead>
          <tr>
            <th>{{ $t('common.id') }}</th>
            <th>{{ $t('purchaseOrder.supplier') }}</th>
            <th>{{ $t('common.status') }}</th>
            <th>{{ $t('common.items') }}</th>
            <th>{{ $t('common.actions') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="orders.length === 0">
            <td colspan="5">
              <div class="empty-state">
                <div class="empty-icon">📋</div>
                <p>No purchase orders found</p>
              </div>
            </td>
          </tr>
          <tr v-for="po in orders" :key="po.id">
            <td><strong class="text-primary">PO-{{ po.id }}</strong></td>
            <td>{{ po.supplier_name }}</td>
            <td>
              <span class="status-badge"
                :class="{
                  'status-pending':  po.status === 'PENDING',
                  'status-received': po.status === 'RECEIVED',
                  'status-voided':   po.status === 'VOIDED',
                  'status-draft':    !['PENDING','RECEIVED','VOIDED'].includes(po.status)
                }">
                {{ po.status }}
              </span>
            </td>
            <td>
              <ul class="item-list" v-if="po.lines && po.lines.length">
                <li v-for="line in po.lines" :key="line.id">
                  {{ line.product_name || getProductName(line.product) }}
                  &times; <strong>{{ line.qty_ordered }}</strong>
                  <span class="item-price">({{ line.unit_cost }}/unit)</span>
                </li>
              </ul>
              <span v-else class="text-muted" style="font-size:0.8rem;">{{ $t('common.noItems') }}</span>
            </td>
            <td>
              <button v-if="po.status === 'PENDING'" class="btn-action btn-action-success"
                @click="receivePO(po)">📥 {{ $t('purchaseOrder.receive') }}</button>
              <button v-if="po.status === 'RECEIVED'" class="btn-action btn-action-danger"
                @click="voidPO(po)">🚫 {{ $t('purchaseOrder.void') }}</button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Pagination -->
    <MainPagination
      v-model:currentPage="currentPage"
      v-model:pageSize="pageSize"
      :total="totalRecords"
    />

    <!-- Add/Edit Modal – nested lines -->
    <div class="modal fade" ref="poModal" tabindex="-1">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <form @submit.prevent="savePO">
            <div class="modal-header">
              <h5 class="modal-title">{{ isEdit ? $t('purchaseOrder.add') : $t('purchaseOrder.add') }}</h5>
              <button type="button" class="btn-close" @click="hideModal"></button>
            </div>
            <div class="modal-body">
              <div v-if="errorMessage" class="alert alert-danger py-2 mb-3">
                {{ errorMessage }}
              </div>
              <div class="mb-3">
                <label>{{ $t('purchaseOrder.supplier') }}</label>
                <select v-model="form.supplier" class="form-select" required>
                  <option v-for="s in suppliers" :key="s.id" :value="s.id">{{ s.name }}</option>
                </select>
              </div>
              <div class="mb-3">
                <label>{{ $t('purchaseOrder.warehouse') }}</label>
                <select v-model="form.warehouse" class="form-select" required>
                  <option v-for="w in warehouses" :key="w.id" :value="w.id">{{ w.name }}</option>
                </select>
              </div>

              <h6>{{ $t('purchaseOrder.lines') }}</h6>
              <div class="table-card-sm">
                <table class="premium-table table">
                  <thead>
                    <tr>
                      <th>{{ $t('purchaseOrder.product') }}</th>
                      <th>{{ $t('purchaseOrder.unitOfMeasure') }}</th>
                      <th>{{ $t('purchaseOrder.qty') }}</th>
                      <th>{{ $t('purchaseOrder.unitCost') }}</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(line, idx) in form.lines" :key="idx">
                      <td>
                        <select v-model="line.product" class="form-select form-select-sm" required>
                          <option v-for="p in products" :key="p.id" :value="p.id">{{ p.name }}</option>
                        </select>
                      </td>

                      <!-- UoM -->
                      <td>
                        <select v-model="line.uom" class="form-select form-select-sm" required>
                          <option disabled value="">UoM</option>
                          <option v-for="u in uoms" :key="u.id" :value="u.id">{{ u.code }}</option>
                        </select>
                        <button type="button" class="btn btn-xs btn-outline-primary ms-1" @click="quickAddBaseUoM"
                          v-if="uoms.length === 0">
                          {{ $t('purchaseOrder.addPCS') }}
                        </button>
                      </td>

                      <!-- Qty in chosen UoM -->
                      <td>
                        <input v-model.number="line.qty_ordered" type="number" min="0.01" step="0.01"
                          class="form-control form-control-sm" @blur="convertLine(line)" required>
                      </td>

                      <td><input v-model.number="line.unit_cost" type="number" step="0.01"
                          class="form-control form-control-sm"></td>
                      <td><button type="button" class="btn-action btn-action-danger"
                          @click="form.lines.splice(idx, 1)">✕</button></td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <button type="button" class="btn btn-sm btn-outline-primary" @click="form.lines.push({})">{{ $t('purchaseOrder.addLine') }}</button>
            </div>
            <div class="modal-footer">
              <button type="button" class="btn btn-secondary" @click="hideModal">{{ $t('common.cancel') }}</button>
              <button type="submit" class="btn btn-primary">{{ $t('common.save') }}</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, onMounted } from 'vue'
import api, { receivePurchaseOrder, voidPurchaseOrder } from '@/api/axios'
import * as bootstrap from 'bootstrap'
import { useI18n } from 'vue-i18n'
import MainPagination from '../components/MainPagination.vue'

const { t } = useI18n()


const orders = ref([])
const search = ref('')
const currentPage = ref(1)
const pageSize = ref(10)
const totalRecords = ref(0)
const suppliers = ref([]); const warehouses = ref([]); const products = ref([])
const form = ref({ supplier: '', warehouse: '', lines: [] })
const isEdit = ref(false)
const errorMessage = ref('')

/* ----- template ref ----- */
const poModal = ref(null)
let modalInstance = null

const uoms = ref([])          // [{id, code, is_base}]
const baseUom = ref(null)     // id of base UoM
const uomsConv = ref({})      // {"12->3": {factor: 12}}

const fetchUoM = async () => {
  const [uRes, cRes] = await Promise.all([
    api.get('/uoms/?pagination=false'),
    api.get('/uoms/conversions/map/')
  ])
  uoms.value = uRes.data
  baseUom.value = uRes.data.find(u => u.is_base)?.id
  uomsConv.value = cRes.data
}

const fetchData = async () => {
  try {
    const [poRes, supRes, whRes, prodRes] = await Promise.all([
      api.get('/purchase-orders/', {
        params: {
          page: currentPage.value,
          page_size: pageSize.value,
          search: search.value
        }
      }),
      api.get('/suppliers/?pagination=false'),
      api.get('/warehouses/?pagination=false'),
      api.get('/products/?pagination=false')
    ])
    orders.value = poRes.data.results
    totalRecords.value = poRes.data.count
    suppliers.value = supRes.data
    warehouses.value = whRes.data
    products.value = prodRes.data
  } catch (err) {
    console.error(err)
  }
}

let searchTimeout = null
watch(search, () => {
  currentPage.value = 1
  clearTimeout(searchTimeout)
  searchTimeout = setTimeout(() => {
    fetchData()
  }, 300)
})

watch([currentPage, pageSize], () => {
  fetchData()
})

onMounted(() => {
  fetchData()
  modalInstance = new bootstrap.Modal(poModal.value)
  fetchUoM()
})

/* helper to resolve product name by ID */
const getProductName = (productId) => {
  const p = products.value.find(p => p.id === productId)
  return p ? p.name : `Product #${productId}`
}

function convertLine(line) {
  if (!line.uom || !line.product) return
  if (line.uom === baseUom.value) {
    line.qty_base = Number(line.qty_ordered)
    return
  }
  const key = `${line.uom}->${baseUom.value}`
  const conv = uomsConv.value[key]
  if (!conv) {
    alert('No conversion to base UoM')
    return
  }
  line.qty_base = Number(line.qty_ordered) * (conv?.factor)
}


const hideModal = () => modalInstance.hide()
const openAddModal = () => {
  isEdit.value = false
  errorMessage.value = ''
  form.value = {
    supplier: '',
    warehouse: '',
    lines: [
      {
        product: products.value[0]?.id || null,
        uom: baseUom.value?.id || null,
        qty_ordered: 1,
        unit_cost: 0
      }
    ]
  }
  modalInstance.show()
}
const savePO = async () => {
  errorMessage.value = ''
  /* --- build clean payload --- */
  const payload = {
    supplier: Number(form.value.supplier),
    warehouse: Number(form.value.warehouse),
    lines: form.value.lines.map(l => ({
      product: Number(l.product),
      qty_ordered: Number(l.qty_ordered),
      unit_cost: Number(l.unit_cost),
      uom:(l.uom)
    }))
  }
  console.log('📤 POST /purchase-orders/', JSON.stringify(payload, null, 2))

  /* --- send --- */
  try {
    await api.post('/purchase-orders/', payload)
    await fetchData()
    hideModal()
  } catch (err) {
    console.error(err)
    errorMessage.value = err.response?.data?.detail || 'Failed to save purchase order.'
  }
}

const receivePO = async (po) => {
  if (!confirm(t('purchaseOrder.confirmReceive'))) return
  try {
    await receivePurchaseOrder(po.id)
    await fetchData()
  } catch (err) {
    console.error(err)
    alert(err.response?.data?.detail || 'Failed to receive purchase order.')
  }
}

const voidPO = async (po) => {
  if (!confirm(t('purchaseOrder.confirmVoid'))) return
  try {
    await voidPurchaseOrder(po.id)
    await fetchData()
  } catch (err) {
    console.error(err)
    alert(err.response?.data?.detail || 'Failed to void purchase order.')
  }
}

const quickAddBaseUoM = async () => {
  await api.post('/uoms/', { code: 'PCS', name: 'Pieces', is_base: true })
  await fetchUoM()
  if (form.value.lines.length && baseUom.value) {
    form.value.lines[0].uom = baseUom.value.id
  }
}
</script>